import type { GetUserRelationsResponse, SocialNotificationItem } from '@mrb91002/shared-types'
import React, { createContext, useContext, useEffect, useState, useMemo, useRef } from 'react'

import { fetchUserRelations } from '../api/secureRequests'
import { UserRelationsContextType } from '../types/types'

import { useAuth } from './AuthContext'

// Extended response type — the API may return extra fields not in the shared type
// package-local
interface ExtendedUserRelationsResponse extends GetUserRelationsResponse {
  unpublishedDrafts?: Array<{ patternId: string; title: string; updatedAt: string }>
  unpublishedProjects?: Array<{ projectId: string; title: string; updatedAt: string }>
}

const UserRelationsContext = createContext<UserRelationsContextType | undefined>(undefined)

export const UserRelationsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, idToken, signOut } = useAuth()

  // --- Use "ScreenNames" for state to match backend ---
  const [favoriteIds, setFavoriteIds] = useState<string[]>([])
  const [followingScreenNames, setFollowingScreenNames] = useState<string[]>([])
  const [followerScreenNames, setFollowerScreenNames] = useState<string[]>([])
  const [unpublishedDrafts, setUnpublishedDrafts] = useState<
    Array<{ patternId: string; title: string; updatedAt: string }>
  >([])
  const [unpublishedProjects, setUnpublishedProjects] = useState<
    Array<{ projectId: string; title: string; updatedAt: string }>
  >([])
  const [notifications, setNotifications] = useState<SocialNotificationItem[]>([])
  const [notificationsLastKey, setNotificationsLastKey] = useState<string | null>(null)
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0)
  const [isModerator, setIsModerator] = useState(false)

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const lastFetchedIdToken = useRef<string | null>(null)

  // Memoized sets for O(1) lookup
  const favoriteSet = useMemo(() => new Set(favoriteIds), [favoriteIds])
  const followingSet = useMemo(() => new Set(followingScreenNames), [followingScreenNames])
  const followerSet = useMemo(() => new Set(followerScreenNames), [followerScreenNames])

  // Helper functions
  const isFavorited = (patternId: string) => favoriteSet.has(patternId)
  const isFollowing = (screenName: string) => followingSet.has(screenName)
  const isFollowedBy = (screenName: string) => followerSet.has(screenName)

  // Fetch all relations at once for the logged-in user (via new endpoint)
  useEffect(() => {
    console.log('[UserRelationsProvider] useEffect: isAuthenticated:', isAuthenticated, 'idToken:', idToken)

    if (!isAuthenticated) {
      setFavoriteIds([])
      setFollowingScreenNames([])
      setFollowerScreenNames([])
      setUnpublishedDrafts([])
      setUnpublishedProjects([])
      setNotifications([])
      setNotificationsLastKey(null)
      setUnreadNotificationCount(0)
      setIsModerator(false)
      lastFetchedIdToken.current = null
      console.log('[UserRelationsProvider] Not authenticated: cleared all sets.')
      return
    }

    if (!idToken) {
      // Don't clear data if idToken is temporarily missing
      console.log('[UserRelationsProvider] No idToken yet, skipping fetch.')
      return
    }

    // Skip if we've already fetched for this token (rare but useful in SPA navigation)
    if (lastFetchedIdToken.current === idToken) {
      console.log('[UserRelationsProvider] Already fetched for this idToken, skipping.')
      return
    }

    // Skip fetch if on create/profile page (user doesn't have profile yet)
    if (window.location.pathname === '/create/profile') {
      console.log('[UserRelationsProvider] On create/profile page, skipping fetch.')
      return
    }

    lastFetchedIdToken.current = idToken
    setLoading(true)
    setError(null)

    console.log('[UserRelationsProvider] Fetching user relations...')
    fetchUserRelations(idToken)
      .then((_rawData) => {
        // The API may return extra fields beyond GetUserRelationsResponse
        const data = _rawData as ExtendedUserRelationsResponse
        console.log('[UserRelationsProvider] fetchUserRelations result:', data)
        setFavoriteIds(Array.isArray(data.favoriteIds) ? data.favoriteIds : [])
        setFollowingScreenNames(Array.isArray(data.followingScreenNames) ? data.followingScreenNames : [])
        setFollowerScreenNames(Array.isArray(data.followerScreenNames) ? data.followerScreenNames : [])
        setUnpublishedDrafts(Array.isArray(data.unpublishedDrafts) ? data.unpublishedDrafts : [])
        setUnpublishedProjects(Array.isArray(data.unpublishedProjects) ? data.unpublishedProjects : [])
        setNotifications(Array.isArray(data.notifications) ? data.notifications : [])
        setNotificationsLastKey(data.notificationsLastKey || null)
        setUnreadNotificationCount(data.unreadNotificationCount || 0)
        setIsModerator(data.isModerator ?? false)
        console.log('[UserRelationsProvider] isModerator from API:', data.isModerator)
        console.log(
          '[UserRelationsProvider] State after fetch:',
          'favoriteIds:',
          Array.isArray(data.favoriteIds) ? data.favoriteIds : [],
          'followingScreenNames:',
          Array.isArray(data.followingScreenNames) ? data.followingScreenNames : [],
          'followerScreenNames:',
          Array.isArray(data.followerScreenNames) ? data.followerScreenNames : [],
        )
      })
      .catch((err) => {
        const errorMessage = typeof err === 'string' ? err : err.message

        // Check if user needs to create a profile.
        // Do NOT redirect here. AuthContext.initAuth is the single source of truth
        // for routing profile-less users to /create/profile. Issuing a second,
        // conflicting redirect (especially a full-page window.location.href to a
        // different setup route) races with AuthContext and causes a redirect loop
        // for brand-new accounts. Just skip relations loading until the profile exists.
        if (errorMessage?.includes('User has not created a profile')) {
          console.log('[UserRelationsProvider] No profile yet; deferring to AuthContext for setup routing.')
          // Allow a later retry once the profile is created (token unchanged).
          lastFetchedIdToken.current = null
          return
        }

        setError(errorMessage)
        console.error('[UserRelationsProvider] fetchUserRelations error:', err)
        if (errorMessage?.includes('401')) signOut()
      })
      .finally(() => {
        setLoading(false)
        console.log('[UserRelationsProvider] Loading done')
      })
  }, [isAuthenticated, idToken, signOut])

  // Optimistic updaters
  const addFavorite = (patternId: string) =>
    setFavoriteIds((ids) => (ids.includes(patternId) ? ids : [...ids, patternId]))
  const removeFavorite = (patternId: string) => setFavoriteIds((ids) => ids.filter((id) => id !== patternId))

  const addFollowing = (screenName: string) =>
    setFollowingScreenNames((ids) => (ids.includes(screenName) ? ids : [...ids, screenName]))
  const removeFollowing = (screenName: string) =>
    setFollowingScreenNames((ids) => ids.filter((id) => id !== screenName))

  const setFavorites = (ids: string[]) => setFavoriteIds(ids)
  const setFollowing = (ids: string[]) => setFollowingScreenNames(ids)
  const setFollowers = (ids: string[]) => setFollowerScreenNames(ids)

  // Force refresh user relations
  const refreshUserRelations = () => {
    if (idToken) {
      lastFetchedIdToken.current = null // Reset to force refetch
      setLoading(true)
      setError(null)

      fetchUserRelations(idToken)
        .then((_rawData) => {
          const data = _rawData as ExtendedUserRelationsResponse
          setFavoriteIds(Array.isArray(data.favoriteIds) ? data.favoriteIds : [])
          setFollowingScreenNames(Array.isArray(data.followingScreenNames) ? data.followingScreenNames : [])
          setFollowerScreenNames(Array.isArray(data.followerScreenNames) ? data.followerScreenNames : [])
          setUnpublishedDrafts(Array.isArray(data.unpublishedDrafts) ? data.unpublishedDrafts : [])
          setUnpublishedProjects(Array.isArray(data.unpublishedProjects) ? data.unpublishedProjects : [])
          setNotifications(Array.isArray(data.notifications) ? data.notifications : [])
          setNotificationsLastKey(data.notificationsLastKey || null)
          setUnreadNotificationCount(data.unreadNotificationCount || 0)
          setIsModerator(data.isModerator ?? false)
          lastFetchedIdToken.current = idToken
        })
        .catch((err) => {
          setError(typeof err === 'string' ? err : err.message)
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }

  // Final context log (will trigger on every render)
  useEffect(() => {
    console.log(
      '[UserRelationsProvider] Current state - favoriteIds:',
      favoriteIds,
      'followingScreenNames:',
      followingScreenNames,
      'followerScreenNames:',
      followerScreenNames,
    )
  }, [favoriteIds, followingScreenNames, followerScreenNames])

  return (
    <UserRelationsContext.Provider
      value={{
        favoriteIds,
        favoriteSet,
        isFavorited,
        followingScreenNames,
        followingSet,
        isFollowing,
        followerScreenNames,
        followerSet,
        isFollowedBy,
        unpublishedDrafts,
        unpublishedProjects,
        notifications,
        notificationsLastKey,
        unreadNotificationCount,
        isModerator,
        loading,
        error,
        addFavorite,
        removeFavorite,
        addFollowing,
        removeFollowing,
        setFavorites,
        setFollowing,
        setFollowers,
        refreshUserRelations,
      }}
    >
      {children}
    </UserRelationsContext.Provider>
  )
}

// ---- Hook ----
export function useUserRelations() {
  const context = useContext(UserRelationsContext)
  if (!context) {
    throw new Error('useUserRelations must be used within a UserRelationsProvider')
  }
  return context
}
